import { parseSizeList } from "@/lib/manufacturing/groupMembers";
import { normalizeSeriesCode, seriesCodeFromName } from "@/lib/manufacturing/seriesWizardPlan";
import { stylesFromRaw } from "./expandBottomUp";
import {
  applyNamingPattern,
  DEFAULT_CODE_PATTERN,
  DEFAULT_NAME_PATTERN,
  styleDisplayKey,
} from "./skuPattern";
import type { ComposeArchetype } from "./types";

export type SellableSkuLine = {
  code: string;
  name: string;
  sizeLabel: string;
  styleKey: string;
};

/**
 * Fan out series name/code × sizes × styles into sellable product SKU lines.
 */
export function buildSellableSkusFromArchetype(input: {
  seriesName: string;
  seriesCode: string;
  namingPattern: string;
  namePattern?: string;
  sizesRaw: string;
  styles: readonly string[] | string;
}): { ok: true; skus: SellableSkuLine[]; skuListRaw: string } | { ok: false; error: string } {
  const seriesName = input.seriesName.trim();
  const seriesCode =
    normalizeSeriesCode(input.seriesCode) ||
    (seriesName ? seriesCodeFromName(seriesName) : "");
  if (!seriesName && !seriesCode) {
    return { ok: false, error: "Series name or series code is required." };
  }
  const code = seriesCode || seriesCodeFromName(seriesName);
  const name = seriesName || code;
  const sizes = parseSizeList(input.sizesRaw);
  if (sizes.length === 0) {
    return { ok: false, error: "Add at least one size (e.g. 8x10, 10x12)." };
  }
  const styles =
    typeof input.styles === "string"
      ? stylesFromRaw(input.styles)
      : input.styles.map((s) => s.trim()).filter(Boolean);
  if (styles.length === 0) {
    return { ok: false, error: "Add at least one style (e.g. Utility, Lofted, Studio)." };
  }

  const codePattern = input.namingPattern.trim() || DEFAULT_CODE_PATTERN;
  const namePattern = (input.namePattern ?? "").trim() || DEFAULT_NAME_PATTERN;
  const skus: SellableSkuLine[] = [];
  for (const sizeLabel of sizes) {
    for (const styleKey of styles) {
      const vars = {
        CODE: code,
        SIZE: sizeLabel,
        STYLE: styleKey,
        SERIES: name,
      };
      skus.push({
        code: applyNamingPattern(codePattern, vars),
        name: applyNamingPattern(namePattern, vars),
        sizeLabel,
        styleKey: styleDisplayKey(styleKey),
      });
    }
  }

  return {
    ok: true,
    skus,
    skuListRaw: skus.map((s) => `${s.code} | ${s.name}`).join("\n"),
  };
}

export function withGeneratedSellableSkus(
  archetype: ComposeArchetype,
  stylesRaw?: string,
): { ok: true; archetype: ComposeArchetype; count: number } | { ok: false; error: string } {
  const built = buildSellableSkusFromArchetype({
    seriesName: archetype.seriesName,
    seriesCode: archetype.seriesCode,
    namingPattern: archetype.namingPattern,
    namePattern: archetype.namePattern,
    sizesRaw: archetype.sizesRaw,
    styles: stylesRaw ?? archetype.styles,
  });
  if (!built.ok) return built;
  return {
    ok: true,
    count: built.skus.length,
    archetype: {
      ...archetype,
      styles:
        typeof stylesRaw === "string"
          ? stylesFromRaw(stylesRaw)
          : archetype.styles,
      skuListRaw: built.skuListRaw,
    },
  };
}
